import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MovimientoService } from "app/services/movimiento.service";
import { MedicoService } from "app/services/medico.service";
import { SpinnerService } from "app/services/spinner.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NotificationComponent } from "app/shared/notification/notification.component";
import { Subscription, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import * as moment from "moment";
import { saveAs } from "file-saver";
import { camposFaltantes, mensajeError } from "app/utils/form-errors.utils";

@Component({
  selector: "app-movimientos-medico",
  templateUrl: "./movimientos-medico.component.html",
  styleUrls: ["./movimientos-medico.component.scss"],
})
export class MovimientosMedicoComponent implements OnInit, OnDestroy {
  medicoId: number;
  medicoNombre: string = "";
  movimientos: any[] = [];
  totalCobrado: number = 0;
  totalGastado: number = 0;
  saldo: number = 0;

  displayedColumns: string[] = [
    "fecha_hora",
    "tipo",
    "monto",
    "descripcion",
    "acciones",
  ];

  gastoForm = new FormGroup({
    fecha: new FormControl(null),
    monto: new FormControl(null, [Validators.required, Validators.min(0.01)]),
    descripcion: new FormControl("", Validators.required),
  });

  mostrarFormulario = false;
  editandoId: number | null = null;

  filtrosForm = new FormGroup({
    fechaDesde: new FormControl(null),
    fechaHasta: new FormControl(null),
    tipo: new FormControl(""),
  });

  private subscripciones = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private movimientoService: MovimientoService,
    private medicoService: MedicoService,
    private spinnerService: SpinnerService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.spinnerService.mostrarSpinner();
    this.medicoId = +this.route.snapshot.paramMap.get("id");

    this.subscripciones.add(
      this.medicoService.mostrar(this.medicoId).pipe(
        map((data: any) => {
          this.medicoNombre = data.medico.nombreCompleto || (data.medico.name + " " + data.medico.surname);
        }),
        switchMap(() => this.movimientoService.listarPorMedico(this.medicoId)),
        map((data) => {
          this.setData(data);
          this.spinnerService.ocultarSpinner();
        }),
        catchError(() => {
          this.spinnerService.ocultarSpinner();
          return of(null);
        }),
      ).subscribe()
    );
  }

  ngOnDestroy() {
    this.subscripciones.unsubscribe();
  }

  filtrar() {
    this.spinnerService.mostrarSpinner();
    const filtros: any = {};
    if (this.filtrosForm.value.fechaDesde) {
      filtros.fechaDesde = moment(this.filtrosForm.value.fechaDesde).format("YYYY-MM-DD");
    }
    if (this.filtrosForm.value.fechaHasta) {
      filtros.fechaHasta = moment(this.filtrosForm.value.fechaHasta).format("YYYY-MM-DD");
    }
    if (this.filtrosForm.value.tipo) {
      filtros.tipo = this.filtrosForm.value.tipo;
    }

    this.subscripciones.add(
      this.movimientoService.listarPorMedico(this.medicoId, filtros).subscribe(
        (data) => {
          this.setData(data);
          this.spinnerService.ocultarSpinner();
        },
        () => this.spinnerService.ocultarSpinner(),
      )
    );
  }

  limpiarFiltros() {
    this.filtrosForm.reset({ fechaDesde: null, fechaHasta: null, tipo: "" });
    this.filtrar();
  }

  exportarExcel() {
    import("xlsx").then((XLSX) => {
      const data = this.movimientos.map((m) => ({
        Fecha: moment(m.fecha_hora).format("DD/MM/YYYY HH:mm"),
        Tipo: m.tipo === "gasto" ? "Gasto" : "Turno",
        Monto: m.monto,
        Descripción: m.descripcion || "",
      }));

      data.push(
        { Fecha: "", Tipo: "", Monto: null, Descripción: "" },
        { Fecha: "", Tipo: "Cobrado", Monto: this.totalCobrado, Descripción: "" },
        { Fecha: "", Tipo: "Gastado", Monto: this.totalGastado, Descripción: "" },
        { Fecha: "", Tipo: "Saldo", Monto: this.saldo, Descripción: "" },
      );

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Movimientos");
      const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      saveAs(blob, `movimientos_${this.medicoNombre.replace(/\s/g, "_")}.xlsx`);
    });
  }

  toggleFormulario() {
    if (this.mostrarFormulario) {
      this.cancelarEdicion();
    } else {
      this.mostrarFormulario = true;
    }
  }

  guardarGasto() {
    if (this.gastoForm.invalid) {
      this.openSnackBar(
        camposFaltantes(this.gastoForm, { monto: "Monto", descripcion: "Descripción" }),
        "error",
      );
      return;
    }

    if (this.editandoId) {
      this.guardarEdicion();
      return;
    }

    this.spinnerService.mostrarSpinner();
    const movimiento: any = {
      tipo: "gasto",
      id_medico: this.medicoId,
      monto: this.gastoForm.value.monto,
      descripcion: this.gastoForm.value.descripcion,
    };
    if (this.gastoForm.value.fecha) {
      movimiento.fecha_hora = moment(this.gastoForm.value.fecha).format("YYYY-MM-DD HH:mm:ss");
    }

    this.subscripciones.add(
      this.movimientoService.crear(movimiento).subscribe(
        () => {
          this.openSnackBar("Gasto registrado", "exito");
          this.gastoForm.reset();
          this.mostrarFormulario = false;
          this.filtrar();
        },
        (err) => {
          this.spinnerService.ocultarSpinner();
          this.openSnackBar(mensajeError(err, "Error al registrar gasto"), "error");
        },
      ),
    );
  }

  editarMovimiento(movimiento: any) {
    this.editandoId = movimiento.id;
    this.gastoForm.patchValue({
      fecha: movimiento.fecha_hora ? new Date(movimiento.fecha_hora) : null,
      monto: movimiento.monto,
      descripcion: movimiento.descripcion,
    });
    this.mostrarFormulario = true;
  }

  guardarEdicion() {
    if (this.gastoForm.invalid) {
      this.openSnackBar(
        camposFaltantes(this.gastoForm, { monto: "Monto", descripcion: "Descripción" }),
        "error",
      );
      return;
    }

    this.spinnerService.mostrarSpinner();
    const data: any = {
      monto: this.gastoForm.value.monto,
      descripcion: this.gastoForm.value.descripcion,
    };
    if (this.gastoForm.value.fecha) {
      data.fecha_hora = moment(this.gastoForm.value.fecha).format("YYYY-MM-DD HH:mm:ss");
    }

    this.subscripciones.add(
      this.movimientoService.editar(this.editandoId, data).subscribe(
        () => {
          this.openSnackBar("Gasto actualizado", "exito");
          this.cancelarEdicion();
          this.filtrar();
        },
        (err) => {
          this.spinnerService.ocultarSpinner();
          this.openSnackBar(mensajeError(err, "Error al actualizar gasto"), "error");
        },
      ),
    );
  }

  cancelarEdicion() {
    this.editandoId = null;
    this.gastoForm.reset();
    this.mostrarFormulario = false;
  }

  eliminarMovimiento(id: number) {
    this.spinnerService.mostrarSpinner();
    this.subscripciones.add(
      this.movimientoService.borrar(id).subscribe(
        () => {
          this.openSnackBar("Movimiento eliminado", "exito");
          this.filtrar();
        },
        (err) => {
          this.spinnerService.ocultarSpinner();
          this.openSnackBar(mensajeError(err, "Error al eliminar"), "error");
        },
      ),
    );
  }

  private openSnackBar(message: string, action: string) {
    this.snackBar.openFromComponent(NotificationComponent, {
      data: {
        message: message[0].toUpperCase() + message.slice(1),
        action: action,
      },
      duration: 2500,
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: [action],
    });
  }

  private setData(data: any) {
    this.movimientos = data.listaMovimientos;
    this.totalCobrado = data.totalCobrado;
    this.totalGastado = data.totalGastado;
    this.saldo = data.saldo;
  }
}
